import { inject, Injectable, signal } from '@angular/core';
import { NavController } from '@ionic/angular';
import { finalize } from 'rxjs';
import { AnalyticsEvents } from '@app/core/enums/analytics-events.enum';
import { ProcessNames, ProcessSteps } from '@app/core/enums/process.enum';
import { Route } from '@app/core/enums/route.enum';
import { AnalyticsService } from '@app/core/services/analytics.service';
import { PolicyService } from '@app/core/services/policy.service';
import { PolicyConditions } from '@app/core/types/policy.types';

/**
 * Re-consent after an update: a registered user whose profile exists is asked
 * to accept a newer terms or privacy policy version. First-time consent is not
 * collected here — registration and share data each include their own
 * checkboxes.
 */
@Injectable({ providedIn: 'root' })
export class ConsentSandbox {
  private readonly policyService = inject(PolicyService);
  private readonly navController = inject(NavController);
  private readonly analyticsService = inject(AnalyticsService);

  termsAndConditions = signal<PolicyConditions | null>(null);
  privacyPolicy = signal<PolicyConditions | null>(null);
  accepting = signal(false);

  loadPolicies(): void {
    this.policyService.getTermsAndConditions().subscribe((terms) => this.termsAndConditions.set(terms));
    this.policyService.getPrivacyPolicy().subscribe((policy) => this.privacyPolicy.set(policy));
  }

  accept(): void {
    const termsId = this.termsAndConditions()?.id;
    if (!termsId || this.accepting()) {
      return;
    }

    this.accepting.set(true);

    this.policyService
      .acceptTermsAndConditions(termsId)
      .pipe(finalize(() => this.accepting.set(false)))
      .subscribe(() => {
        this.analyticsService.logEvent(AnalyticsEvents.ProcessCompleted, {
          name: ProcessNames.ConsentUpdate,
        });
        this.navController.navigateRoot([Route.Home]);
      });
  }

  logConsentUpdateStart(): void {
    this.analyticsService.logEvent(AnalyticsEvents.Process, {
      name: ProcessNames.ConsentUpdate,
      step: ProcessSteps.ConsentUpdateStart,
    });
  }
}
